import { MAX_ZONE_TYPES } from "../document/zones.ts";

/**
 * The zone types a game registers with the editor (ADR-054's amendment of 2026-09-27) and the check
 * `registerAdapter` runs over them.
 */

/**
 * A zone type a game registers (ADR-054's amendment of 2026-09-27, decision 1): its id, lower-case letters,
 * digits and `-`, and the label the author reads. Its place in the registration is its overlay category and
 * number; what it means is the game's code, keyed by the id, which Daedalus never reads. Optionally (ADR-054's
 * amendment of 2026-10-01): a one-line `description` of what the type means, which the Zones palette shows as
 * the type's tooltip, and `unpainted`, marking the one type unpainted ground plays as, which the zones legend
 * names. Daedalus shows both and reads neither.
 */
export interface ZoneTypeDefinition {
  readonly id: string;
  readonly label: string;
  readonly description?: string;
  readonly unpainted?: boolean;
}

const ZONE_TYPE_ID = /^[a-z0-9-]+$/;

/**
 * The most characters a zone type's description may hold: one line of a tooltip.
 */
export const MAX_ZONE_DESCRIPTION = 160;

/**
 * Checks a game's zone types: each id non-empty and made of lower-case letters, digits and `-`, none repeated,
 * each label not blank, each description, when given, not blank and at most `MAX_ZONE_DESCRIPTION` characters,
 * at most one type marked `unpainted`, and at most `MAX_ZONE_TYPES` types, since a zone cell holds a byte.
 *
 * @param types The types, in registration order.
 * @throws Error naming `zoneTypes` and the offending type.
 */
export function validateZoneTypes(types: readonly ZoneTypeDefinition[]): void {
  if (types.length > MAX_ZONE_TYPES) {
    throw new Error(`zoneTypes: ${types.length} types registered; at most ${MAX_ZONE_TYPES}`);
  }
  const seen = new Set<string>();
  let unpainted: string | null = null;
  for (const type of types) {
    if (typeof type.id !== "string" || !ZONE_TYPE_ID.test(type.id)) {
      throw new Error(`zoneTypes: ${String(type.id)} is not an id of lower-case letters, digits and -`);
    }
    if (seen.has(type.id)) {
      throw new Error(`zoneTypes: ${type.id} is registered twice`);
    }
    seen.add(type.id);
    if (typeof type.label !== "string" || type.label.trim() === "") {
      throw new Error(`zoneTypes: ${type.id} has an empty label`);
    }
    if (type.description !== undefined) {
      if (typeof type.description !== "string" || type.description.trim() === "") {
        throw new Error(`zoneTypes: ${type.id} has a blank description`);
      }
      if (type.description.length > MAX_ZONE_DESCRIPTION) {
        throw new Error(`zoneTypes: ${type.id} has a description of ${type.description.length} characters; at most ${MAX_ZONE_DESCRIPTION}`);
      }
    }
    if (type.unpainted === true) {
      if (unpainted !== null) {
        throw new Error(`zoneTypes: ${type.id} is the unpainted type, and so is ${unpainted}; at most one`);
      }
      unpainted = type.id;
    }
  }
}
